import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useForm } from 'react-hook-form'
import classnames from 'classnames'
import { Link } from 'react-router-dom'

import { onResetPasswordDispatch as dispatchSelector } from 'reduxModules/auth/dispatchers'
import { validatePasswordField, isPasswordValid } from '../../../utils/passwordRules'
import PasswordStrength from '../../generic/PasswordStrength'
import withRouterV6 from '../../../utils/withRouterV6'
import keyUpHandler from '../../utils/keyUpHandler'
import InlineError, { ariaErrorProps } from '../../generic/InlineError'
import RestrictedAccountNotice from '../../generic/RestrictedAccountNotice'
import '../../generic/RenderedField/styles.css'

export function ResetPassword({ onSubmit, hideGlobalNav, showGlobalNav }) {
  const [password1Visible, setPassword1Visible] = useState(false)
  const [password2Visible, setPassword2Visible] = useState(false)
  // SPEC-119 D3: a terminated account proving ownership through the link
  const [restricted, setRestricted] = useState(false)
  const {
    register,
    handleSubmit,
    watch,
    formState: { isSubmitting, errors, isDirty }
  } = useForm()

  const password1 = watch('password1')

  useEffect(() => {
    hideGlobalNav()
    return () => showGlobalNav()
  }, [hideGlobalNav, showGlobalNav])

  return (
    <div id="ResetPassword" className="auth-page">
      <div>
        <div className="logo">
          <Link to="/">
            <img src="/global/assets/images/logo/logo-big.png" alt="Stan Winston School Of Character Arts" />
          </Link>
        </div>

        <div className="reset-password auth-form-box">
          <h1>Reset Password</h1>
          <form
            id="SignInForm"
            onSubmit={handleSubmit((data) =>
              Promise.resolve(onSubmit(data)).then((result) => {
                if (result?.restricted) setRestricted(true)
              })
            )}
          >
            <div className={classnames('field', '-with-feedback', '-floating', { '-error': errors.password1 })}>
              <div className="field-container">
                <input
                  {...register('password1', {
                    required: 'This field is required.',
                    validate: validatePasswordField
                  })}
                  type={password1Visible ? 'text' : 'password'}
                  placeholder=" "
                  id="reset-new-password"
                  {...ariaErrorProps('reset-new-password', errors.password1)}
                />
                <label htmlFor="reset-new-password">New Password</label>
                <i
                  className={classnames('icon', password1Visible ? 'icon-passcodewatch' : 'icon-passcodewatch disabled')}
                  aria-label="Toggle password visibility"
                  onClick={() => setPassword1Visible(!password1Visible)}
                  onKeyUp={event => keyUpHandler(event, () => setPassword1Visible(!password1Visible))}
                  tabIndex="0"
                />
                <InlineError id="reset-new-password" message={errors.password1?.message} />
              </div>
            </div>
            <PasswordStrength password={password1} similarityTip />
            <div className={classnames('field', '-floating', { '-error': errors.password2 })}>
              <div className="field-container">
                <input
                  {...register('password2', {
                    required: 'This field is required.',
                    validate: value => value === password1 || "passwords don't match"
                  })}
                  type={password2Visible ? 'text' : 'password'}
                  placeholder=" "
                  id="reset-retype-password"
                  {...ariaErrorProps('reset-retype-password', errors.password2)}
                />
                <label htmlFor="reset-retype-password">Retype New Password</label>
                <i
                  className={classnames('icon', password2Visible ? 'icon-passcodewatch' : 'icon-passcodewatch disabled')}
                  aria-label="Toggle password visibility"
                  onClick={() => setPassword2Visible(!password2Visible)}
                  onKeyUp={event => keyUpHandler(event, () => setPassword2Visible(!password2Visible))}
                  tabIndex="0"
                />
                <InlineError id="reset-retype-password" message={errors.password2?.message} />
              </div>
            </div>
            <button
              type="submit"
              className="btn -wide -blue-2"
              disabled={!isDirty || isSubmitting || !isPasswordValid(password1)}
            >
              Save
            </button>
          </form>

          {restricted && <RestrictedAccountNotice />}

          <div className="auth-alt">
            or <Link to="/login">Sign In</Link>
          </div>
        </div>
      </div>
    </div>
  )
}

ResetPassword.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  hideGlobalNav: PropTypes.func.isRequired,
  showGlobalNav: PropTypes.func.isRequired
}

export default withRouterV6(connect(
  null,
  dispatchSelector
)(ResetPassword))
